import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { ModelStoreAbstraction } from "@bitwarden/web-vault/app/services/store/model.store.abstraction";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { EstimateStoreCollection } from "@bitwarden/web-vault/app/services/store/scenario/estimate.store.collection";
import { EstimateActionStoreCollection } from "@bitwarden/web-vault/app/services/store/scenario/estimate.action.store.collection";
import {
  ModelCollectionInterface,
  ScenarioStoreCollectionType,
  StoreModelCollection,
} from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { ScenarioStoreCollection } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.collection";
import { ScenarioGroupStoreCollection } from "@bitwarden/web-vault/app/services/store/scenario/scenario-group.store.collection";
import { ScenarioDataGeneratedCollection } from "@bitwarden/web-vault/app/services/store/scenario/scenario-data.generated.collection";
import { ScenarioResults } from "@bitwarden/web-vault/app/models/types/scenario-group.types";
import { ScenarioService } from "@bitwarden/web-vault/app/services/DataCalculationService/scenarios/scenario.service";
import { ScenarioGroupView } from "@bitwarden/web-vault/app/models/view/scenario-group/scenario-group.view";
import { ScenarioView } from "@bitwarden/web-vault/app/models/view/scenario/scenario.view";
import {
  setAnchorDate,
  setEndDate,
} from "@bitwarden/web-vault/app/shared/utils/helper.scenarioGroupView/scenarioDates";
import { DefaultScenarios } from "@bitwarden/web-vault/app/services/store/scenario/defaultScenarios.util";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import {
  BehaviorSubject,
  merge,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  skip,
  skipUntil,
  combineLatest,
  Subscription,
  Subject,
  debounceTime,
} from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import {
  CalculatedBalanceParams,
  ScenarioBalanceParams,
} from "@bitwarden/web-vault/app/services/store/calculated-balance.collection.abstraction";

import { ScenarioBalanceByTimeCalculatedCollection } from "@bitwarden/web-vault/app/services/store/scenario/balances/scenario-balance-by-time.calculated.collection";
import { ScenarioBalanceByTimeAndAccountsCalculatedCollection } from "@bitwarden/web-vault/app/services/store/scenario/balances/scenario-balance-by-time-and-accounts.calculated.collection";
import { DefaultCurrencyPerLocation } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { ScenarioPeriodController } from "@bitwarden/web-vault/app/services/store/scenario/controls/period.control";
import { ScenarioFilterControl } from "@bitwarden/web-vault/app/services/store/scenario/controls/filter.control";
import { ScenarioBalanceByAccountsAndSymbolsCalculatedCollection } from "@bitwarden/web-vault/app/services/store/scenario/balances/scenario-balance-by-accounts-and-symbol.calculated.collection";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";
import { BalanceAlignmentTransactionsGeneratedCollection } from "@bitwarden/web-vault/app/services/store/calculation-base/generated-transactions/balance-alignment-transactions.generated.collection";
import { GeneratedTransactionParams } from "@bitwarden/web-vault/app/services/store/generated.collection.abstraction";
import { FilteredAccountGeneratedCollection } from "@bitwarden/web-vault/app/services/store/calculation-base/filtered-account.generated.collection";
import { ScenarioGroupControl } from "@bitwarden/web-vault/app/services/store/scenario/controls/group.control";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { View as ViewEnum } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ScenarioTypes } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";
import { NavigationEnd, Router } from "@angular/router";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { ScenarioBalanceByTransactionsCalculatedCollection } from "@bitwarden/web-vault/app/services/store/scenario/balances/scenario-balance-by-transactions.calculated.collection";

interface ScenarioStoreState {
  status:
    | "Initialise"
    | "Idle"
    | "UpdatingParameters"
    | "UpdatingControls"
    | "UpdatingCalculation"
    | "Completed"
    | "Error";
}

interface ScenarioStorePage {
  isFilteringOptionsDirty: boolean;
  isParameterDirty: boolean;
  isAnchorBalanceDirty: boolean;
  isScenarioDataDirty: boolean;
}

const StateIsDirty: ScenarioStorePage = {
  isAnchorBalanceDirty: true,
  isFilteringOptionsDirty: true,
  isParameterDirty: true,
  isScenarioDataDirty: true,
};

@Injectable({
  providedIn: "root",
})
export class ScenarioStoreService
  extends ModelStoreAbstraction
  implements ModelCollectionInterface<ScenarioStoreCollectionType>
{
  /** Services **/
  protected log: LogService = inject(LogService);
  private router = inject(Router);
  private scenarioService = inject(ScenarioService);
  private transactionStore = inject(TransactionStoreService);
  private userStoreService = inject(UserStoreService);
  private viewService = inject(ViewsService);
  private appState: AppStateStoreService = inject(AppStateStoreService);
  private dataPluginStore = inject(DataPluginStoreService);

  /** Collection  **/
  /** This contains all the transaction with the filter applied to be used for balance calculation **/
  readonly filteredAccounts = new FilteredAccountGeneratedCollection();

  readonly scenario: ScenarioStoreCollection = new ScenarioStoreCollection();
  readonly scenarioGroup: ScenarioGroupStoreCollection = new ScenarioGroupStoreCollection();
  readonly estimateAction: EstimateActionStoreCollection = new EstimateActionStoreCollection();
  readonly estimate: EstimateStoreCollection = new EstimateStoreCollection();
  readonly balanceByTime: ScenarioBalanceByTimeCalculatedCollection =
    new ScenarioBalanceByTimeCalculatedCollection();
  readonly balanceByTimeAndAccounts: ScenarioBalanceByTimeAndAccountsCalculatedCollection =
    new ScenarioBalanceByTimeAndAccountsCalculatedCollection();
  readonly balanceByAccountSymbols: ScenarioBalanceByAccountsAndSymbolsCalculatedCollection =
    new ScenarioBalanceByAccountsAndSymbolsCalculatedCollection();
  readonly balanceByTransaction: ScenarioBalanceByTransactionsCalculatedCollection =
    new ScenarioBalanceByTransactionsCalculatedCollection();
  readonly balanceAlignmentTransactions = new BalanceAlignmentTransactionsGeneratedCollection();
  readonly interestScenario: ScenarioDataGeneratedCollection =
    new ScenarioDataGeneratedCollection();

  currentScenarioType: WritableSignal<ScenarioTypes> = signal<ScenarioTypes>(
    ScenarioTypes.BestNewInterestRate,
  );

  /** Store controls **/
  period = new ScenarioPeriodController();
  filters = new ScenarioFilterControl();
  groupings = new ScenarioGroupControl();

  /** Store trigger dependencies **/
  private transactions$ = this.transactionStore.enrichTransaction.collection$;
  private accounts$ = this.userStoreService.enrichAccounts.collection$;
  private institutions$ = toObservable(this.dataPluginStore.theInstitutionList.theInstitutionViews);
  private preference$ = toObservable(this.userStoreService.preferences.preferenceView);
  private selectedView$ = toObservable(this.viewService.currentView);
  private wizard$ = toObservable(this.appState.wizard);

  /** Store internal loading state **/
  protected _storeState$: BehaviorSubject<ScenarioStoreState> = new BehaviorSubject({
    status: "Initialise",
  });
  storeState$ = this._storeState$.asObservable();

  /**
   * Durable readiness latch: `true` once a scenario cycle has settled (success, empty, or error).
   * Unlike {@link storeState$} — whose transient `Completed` status is synchronously overwritten with
   * `Idle` in {@link refreshScenarioData} — this rests at its last value, so a late subscriber (e.g.
   * ViewReadinessService resubscribing after an SPA logout→login) still sees readiness. Mirrors the
   * calc store's resting `isCalculationReady$`. Reset in {@link clearStore} so the next session re-gates.
   * See PR #3708.
   */
  private readonly _isScenarioReady$ = new BehaviorSubject<boolean>(false);
  readonly isScenarioReady$ = this._isScenarioReady$.asObservable();

  /** Bumped by clearStore(). clearStore() can't cancel an in-flight refresh, so a refresh takes a
   * staleCheck() before its first await and checks it after each one: a refresh from before the
   * clear (e.g. the previous user's, on logout) must not write interestScenario into the cleared
   * store, nor move status / isScenarioReady$ off the Initialise / false that clearStore() reset
   * them to, which would signal the next session ready before its own data has loaded. The
   * balance collections guard their own writes the same way (CalculatedBalanceCollection). */
  private storeEpoch = 0;
  /** Set while updateDefaultScenario() hasn't completed since it was asked for: a refresh that
   * failed part-way through it is retried by the next refresh, whatever that one marks dirty. */
  private parametersPending = false;

  /** Internal Variables **/
  private subscriptions: Subscription[] = [];
  webWorkerQueue = WebWorkerQueue.getInstance();

  private pathTabClickedSubject = new Subject<ScenarioTypes>();
  pathTabClicked$ = this.pathTabClickedSubject.asObservable();
  emitPathTabClicked(value: ScenarioTypes) {
    this.pathTabClickedSubject.next(value);
  }

  initialize(): void {
    this.log.info("Initializing ScenarioStoreService");
    this.initialiseCollectionListener();
  }

  private initialiseCollectionListener(): void {
    this.subscribeToViewNavigationChange();
    this.subscribeToDataChange();
    this.subscribeToControlsChange();
    this.subscribeToWizardCompletion();
  }

  private subscribeToViewNavigationChange() {
    const futureIsSelected$ = this.selectedView$.pipe(
      distinctUntilChanged(),
      filter((x) => x === ViewEnum.FUTURE),
    );

    const viewIsNavigated$ = this.router.events.pipe(
      distinctUntilChanged(),
      filter((x) => x instanceof NavigationEnd && x.url === `/${GlossRoutes.Views}`),
    );
    this.subscriptions.push(
      merge(futureIsSelected$, viewIsNavigated$).subscribe(this.onViewNavigationChanged.bind(this)),
    );
  }

  private subscribeToWizardCompletion() {
    const wizardCompleted$ = this.wizard$.pipe(
      distinctUntilChanged(),
      filter((x) => x.isCompleted),
    );
    this.subscriptions.push(wizardCompleted$.subscribe(this.onWizardCompleted.bind(this)));
  }

  /**
   * Subscription for external data change in other stores
   **/
  private subscribeToDataChange() {
    /** First the user store is loaded and the location will be available **/
    const preferenceChange$ = this.preference$.pipe(
      filter((x) => !!x || !!x?.userLocation),
      distinctUntilKeyChanged("userLocation"),
    );
    this.subscriptions.push(preferenceChange$.subscribe(this.onUserParametersChanged.bind(this)));

    /** Account ( and institution ) from the user store will also be available **/
    const accountChange$ = this.accounts$.pipe(
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
      skipUntil(preferenceChange$),
    );
    this.subscriptions.push(accountChange$.subscribe(this.onAccountCollectionChanged.bind(this)));

    /** Insitution Data must be loaded before we can proceed with scenarios **/

    const institutionChange$ = this.institutions$.pipe(
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
      skipUntil(preferenceChange$),
    );
    this.subscriptions.push(
      institutionChange$.subscribe(this.onInstitutionCollectionChanged.bind(this)),
    );

    const transactionChange$ = this.transactions$.pipe(
      skip(1), // this skip must be added so that we don't use the cached value of transactions after a vault purge
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
    );
    this.subscriptions.push(transactionChange$.subscribe(this.onTransactionsChanged.bind(this)));

    // Only when all the data is loaded we can proceed with the scenario calculations by moving the status to Idle
    const dataSubscriptions = combineLatest([
      accountChange$,
      institutionChange$,
      transactionChange$,
    ]).subscribe(this.onDataCollectionChanged.bind(this));

    this.subscriptions.push(dataSubscriptions);
  }

  /**
   * Subscription to listen on controls changes like filters/period/groupings
   * @private
   */
  private subscribeToControlsChange() {
    const filterSelection$ = this.filters.selected$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => {
        return JSON.stringify(previous) === JSON.stringify(current);
      }),
    );

    const periodSelection$ = this.period.selected$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => {
        return JSON.stringify(previous) === JSON.stringify(current);
      }),
      skipUntil(filterSelection$),
    );

    // Initialize grouping options based on the initial period
    this.groupings.updateOptionAndSelectionFromPeriod(this.period.selected());

    // Update grouping options when period changes, but don't trigger recalculation here
    // The recalculation will be handled by the merged control changes stream below
    this.subscriptions.push(
      periodSelection$.subscribe((newPeriod) => {
        this.groupings.updateOptionAndSelectionFromPeriod(newPeriod);
      }),
    );

    const granularitySelection$ = this.groupings.selected$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => {
        return JSON.stringify(previous) === JSON.stringify(current);
      }),
      skipUntil(periodSelection$),
    );

    // Merge all control changes and debounce to prevent duplicate calculations
    // when period changes trigger grouping changes (cascading updates)
    const allControlChanges$ = merge(
      filterSelection$,
      periodSelection$,
      granularitySelection$,
    ).pipe(
      debounceTime(0), // Batch synchronous changes together
    );

    this.subscriptions.push(allControlChanges$.subscribe(this.onViewControlsChanged.bind(this)));
  }

  private async onUserParametersChanged() {
    this.log.info("[SCENARIO] Change Detected **** Preferences User Location ****");
    await this.refreshState(StateIsDirty);
  }

  private async onAccountCollectionChanged() {
    this.log.info("[SCENARIO] Change Detected **** User Store Enrich Account Data Collection ****");
    await this.refreshState({
      ...StateIsDirty,
      isParameterDirty: false,
    });
  }

  private async onTransactionsChanged() {
    this.log.info(
      "[SCENARIO] Change Detected **** Transaction Store Enrich Transaction Data Collection ****",
    );
    await this.refreshState({
      ...StateIsDirty,
      isParameterDirty: false,
    });
  }

  private async onInstitutionCollectionChanged() {
    this.log.info("[SCENARIO] Change Detected **** Institution Collection ****");
    await this.refreshState({
      ...StateIsDirty,
      isParameterDirty: false,
    });
  }

  // This is the final data collection that is required to be triggered before we can
  // run the scenarios. If this is triggered then we can move the status of the scenario
  // store from initialise to idle
  private async onDataCollectionChanged() {
    this.log.info("[SCENARIO] Change Detected **** Dependent Data Collection Complete ****");

    if (this._storeState$.value.status === "Initialise") {
      this._storeState$.next({ status: "Idle" });
      await this.refreshState(StateIsDirty, true);
    } else {
      await this.refreshState({
        ...StateIsDirty,
        isParameterDirty: false,
      });
    }
  }

  private async onWizardCompleted() {
    this.log.info("[SCENARIO] Change Detected **** Wizard Complete ****");
    await this.refreshState(StateIsDirty);
  }

  private async onViewNavigationChanged() {
    this.log.info("[SCENARIO] Change Detected **** Navigate to Future View ****");
    if (this._storeState$.value.status === "Initialise") {
      this.log.info("[SCENARIO] *** Still Initialising Store ***");
      this.log.info("[SCENARIO] *** Data loading not complete, skipping refresh ***");
    } else {
      await this.refreshState({
        ...StateIsDirty,
        isFilteringOptionsDirty: false,
        isParameterDirty: false,
      });
    }
  }
  private async onViewControlsChanged() {
    this.log.info("Change Detected **** Controls ****");
    await this.refreshState({
      ...StateIsDirty,
      isFilteringOptionsDirty: false,
      isParameterDirty: false,
    });
  }

  private futureViewInFocus() {
    return (
      this.router.url === `/${GlossRoutes.Views}` &&
      this.viewService.currentView() === ViewEnum.FUTURE
    );
  }

  private showingWizard(): boolean {
    if (this.appState.wizard().isVisible && !this.appState.wizard().isCompleted) {
      return true;
    }
    const accounts = this.userStoreService.enrichAccounts.collection();
    return accounts?.length === 0;
  }

  private async refreshState(page: ScenarioStorePage, ignoreViewFocus = false) {
    /** The scenario calculation only runs if the future page is in focus **/

    // if the status of the store is idle and we need to update the parameters, then trigger updateDefaultScenario
    if (this._storeState$.value.status === "Idle") {
      const isStale = this.staleCheck();
      const appSession = this.appState.loadingSession();
      let processStarted = false;
      try {
        if (page.isParameterDirty || this.parametersPending) {
          this.parametersPending = true;
          this._storeState$.next({ status: "UpdatingParameters" });
          this.updateDefaultScenario();
          this.parametersPending = false;
        }

        if (page.isFilteringOptionsDirty) {
          this.updateFiltersOptions();
        }

        if (this.futureViewInFocus() || ignoreViewFocus) {
          if (!this.showingWizard()) {
            this._storeState$.next({ status: "UpdatingControls" });
            this.appState.startProcess("scenario", "Calculating Paths");
            processStarted = true;

            this._storeState$.next({ status: "UpdatingCalculation" });

            if (page.isAnchorBalanceDirty) {
              await this.updateAnchorBalance();
              if (this.isStaleRefresh(isStale, appSession)) {
                return;
              }
            }

            if (page.isScenarioDataDirty) {
              await this.refreshScenarioData();
            }
          } else {
            this.log.info("[SCENARIO] - WIZARD is in process, skipping refresh");
            this._storeState$.next({ status: "Idle" });
          }
        } else {
          this.log.info("[SCENARIO] - Scenario not in Focus, skipping refresh");
          this._storeState$.next({ status: "Idle" });
        }
      } catch (error) {
        // Anything that throws once the store has left Idle (a realign or balance worker rejecting
        // in updateAnchorBalance(), or a synchronous throw) fails the refresh, rather than leave the
        // store busy and every later refresh skipped as "not Idle" (#6276). refreshScenarioData()
        // fails its own run's errors.
        this.log.error(error);
        if (isStale()) {
          // Only after an await, so "Calculating Paths" had started: bring it down, write nothing.
          if (processStarted) {
            this.isStaleRefresh(isStale, appSession);
          }
          return;
        }
        if (
          this._storeState$.value.status === "Idle" &&
          !((this.futureViewInFocus() || ignoreViewFocus) && !this.showingWizard())
        ) {
          // Thrown while updating the filter options, with no calculation going to run (the Future
          // view not in focus, or the wizard showing): there's no refresh to fail.
          return;
        }
        this.failRefresh(processStarted ? () => this.appState.endProcess("scenario") : () => {});
      }
    } else {
      this.log.info("[SCENARIO] - Scenario is not Idle, skipping refresh");
      this.log.info(`[SCENARIO] - State => ${this._storeState$.value.status}`);
    }
  }

  /**.../filters.updateSelection/initFilterListener/...triggerRefresh*/
  private updateFiltersOptions() {
    this.log.info("[SCENARIO] *** setInitialFilters update ***");
    const transactions = this.transactionStore.enrichTransaction.collection();
    // check that transactions is not set to null. This shouldn't happen but sometimes can after a vault purge.
    if (transactions) {
      const accounts = this.userStoreService.enrichAccounts.collection();
      const institutions = this.userStoreService.institutions.institutionViews();
      const preference = this.userStoreService.preferences.preferenceView();
      this.filters.initialiseOptions(transactions, accounts, institutions, preference);
      this.filters.updateSelection(this.filters.options());
    }
  }

  protected updateDefaultScenario() {
    this.log.info("[SCENARIO] 1 - *** Update Default Scenario ***");

    this.interestScenario.clear();
    const defaultScenarioUtil = new DefaultScenarios();

    const symbol =
      DefaultCurrencyPerLocation[this.userStoreService.preferences.preferenceView().userLocation];
    const defaultScenarios = defaultScenarioUtil.createDefaultScenarios(symbol);
    // save the default scenarios to the store
    this.scenario.populateWithSystemDefaults(defaultScenarios);

    // create the default scenario grouping
    const defaultScenarioGroups = defaultScenarioUtil.createDefaultScenarioGroup(
      symbol,
      defaultScenarios,
    );

    // save the grouping to the store
    this.scenarioGroup.populateWithSystemDefaults(defaultScenarioGroups);
  }

  private async updateAnchorBalance() {
    this.log.info("[SCENARIO] 2 - *** Update Anchor Balance ***");
    // Taken before the realign await: balanceByAccountSymbols only takes its own check when
    // updateCalculations() starts, which would already see the next session's epoch.
    const isStale = this.staleCheck();
    const transactions = this.transactionStore.enrichTransaction
      .collection()
      ?.map((t) => t.clone());
    const filteredTransactions = this.filters
      .applyFiltersOnTransaction(transactions)
      ?.sort(sortTransactionViews);

    const accounts = this.userStoreService.enrichAccounts.collection();
    this.filteredAccounts.update(this.filters.applyFiltersOnAccounts(accounts));

    await this.realignBalanceTransactions(filteredTransactions);
    if (isStale()) {
      // A clear landed during the realign: don't apply the alignment or write the previous
      // session's starting balances. refreshState()'s check right after this discards the refresh.
      return;
    }
    const calculatedTransactions = this.balanceAlignmentTransactions.applyBalanceAlignment(
      filteredTransactions,
      this.transactionStore.referenceData.referenceDataViews(),
    );

    const params: CalculatedBalanceParams = {
      period: this.period.selected(),
      calculatedTransactions: calculatedTransactions,
      filter: this.filters.selected(),
      group: this.groupings.selected(),
      webWorkerQueue: this.webWorkerQueue,
    };
    await this.balanceByAccountSymbols.updateCalculations(params);
  }

  async refreshScenarioData() {
    this.log.info("[SCENARIO] 3 - *** Refresh Scenario Data ***");
    const scenarioGroup = this.scenarioGroup.scenarioGroupView();
    if (!Array.isArray(scenarioGroup) || !(scenarioGroup[0] instanceof ScenarioGroupView)) {
      // refreshState() started "Calculating Paths"; nothing to run, so end it here too.
      this.appState.endProcess("scenario");
      this._storeState$.next({ status: "Completed" });
      this._storeState$.next({ status: "Idle" });
      this._isScenarioReady$.next(true);
      return;
    }

    const isStale = this.staleCheck();
    const appSession = this.appState.loadingSession();
    // Set once this refresh has completed, errored or been discarded. Its "scenario" process is
    // ended at most once: a second endProcess() would end the next session's, which startProcess()
    // merges into the same entry.
    let finished = false;
    const end = () => {
      if (!finished) {
        finished = true;
        this.appState.endProcess("scenario");
      }
    };
    // Over: discarded as stale, or already completed / failed (the run's .catch may fail it first).
    const isOver = () => discardIfStale() || finished;
    const discardIfStale = () => {
      if (!isStale()) {
        return false;
      }
      if (!finished) {
        finished = true; // whether or not isStaleRefresh ends the process (it doesn't after a logout)
        this.isStaleRefresh(isStale, appSession);
      }
      return true;
    };
    this.runScenarioGroup(
      scenarioGroup[0],
      async (scenarioWinners) => {
        if (isOver()) {
          return;
        }
        try {
          this.interestScenario.update(scenarioWinners);
          this.interestScenario.enrichTransactions();
          await this.updateBalanceCalculations();
        } catch (error) {
          // Usually nothing else would see this: ScenarioOption calls the callback without awaiting
          // it, once runScenarioGroup()'s promise has resolved (results come back through worker
          // callbacks), so the .catch below doesn't get it (#6276). Where the run does await it
          // (no permutations, or a group ending at its anchor), it's handled here, not rethrown.
          this.log.error(error);
          if (!isOver()) {
            this.failRefresh(end);
          }
          return;
        }
        if (isOver()) {
          return;
        }
        this._storeState$.next({ status: "Idle" });
        end();
        /** Wizard scenario popup is listening to the completed status before trigggering the popup **/
        this._storeState$.next({ status: "Completed" });
        this._storeState$.next({ status: "Idle" });
        this._isScenarioReady$.next(true);
      },
      // Superseded by a newer run (the next session's first refresh): this run's callback never
      // fires, so wind the refresh down here. A no-op once it has finished.
      () => void discardIfStale(),
    ).catch((error) => {
      this.log.error(error);
      if (isOver()) {
        return;
      }
      this.failRefresh(end);
    });
  }

  /** Winds down a refresh that failed and is still this session's (callers log the error and drop a
   * stale or already finished refresh first). `end` ends its "scenario" process, if it started one.
   * The status reports Error, then goes back to Idle, as CalculationBaseStoreService does, so the
   * next change refreshes again instead of being skipped as "not Idle" until the next clearStore(). */
  private failRefresh(end: () => void) {
    end();
    this._storeState$.next({ status: "Error" });
    this._storeState$.next({ status: "Idle" });
    // Latch ready on failure too (isScenarioReady$ latches on success, empty or error), so the
    // Future skeleton doesn't hang forever on an errored cycle.
    this._isScenarioReady$.next(true);
  }

  /** Returns a check that turns true once clearStore() has run since this call. */
  private staleCheck(): () => boolean {
    const epoch = this.storeEpoch;
    return () => epoch !== this.storeEpoch;
  }

  /** True if clearStore() has run since `isStale` was taken. A refresh that outlived the clear then
   * stops without touching the store or its status, and its "Calculating Paths" comes down, as
   * TransactionStoreService does for its own — unless appState.clearStore() (logout) has run since
   * `appSession` was read: that drops every process, so the "scenario" entry by then is the next
   * session's, not this refresh's. reloadFromVault() keeps appState, so there it's still ours. */
  private isStaleRefresh(isStale: () => boolean, appSession: number): boolean {
    if (!isStale()) {
      return false;
    }
    this.log.info("[SCENARIO] Refresh outlived a store clear — discarding it");
    if (this.appState.loadingSession() === appSession) {
      this.appState.endProcess("scenario");
    }
    return true;
  }

  private async realignBalanceTransactions(transactions: TransactionView[]) {
    const parameters: GeneratedTransactionParams = {
      transactions,
      webWorkerQueue: this.webWorkerQueue,
    };
    await this.balanceAlignmentTransactions.generateTransactions(
      parameters,
      this.transactionStore.referenceData.referenceDataViews(),
    );
  }

  private async runScenarioGroup(
    scenarioGroup: ScenarioGroupView,
    callback: (scenarioWinners: ScenarioResults[]) => Promise<void>,
    onSuperseded?: () => void,
  ): Promise<void> {
    const scenarioGroupCopy = scenarioGroup.clone();

    //TODO: @mmeshel - when we have a place in the UI to set anchor date and end date of the scenario graph we need to
    // set them here as parameters
    setAnchorDate(scenarioGroupCopy);
    setEndDate(scenarioGroupCopy, this.period.selected().endDate);

    const scenarioViews = this.scenario.scenarioView();
    const scenarios: ScenarioView[] = scenarioGroup.scenariosId.reduce((acc, s) => {
      const sv = scenarioViews.find((sv) => sv.id === s);
      if (sv) {
        acc.push(sv);
      }
      return acc;
    }, []);

    if (scenarios.length === 0) {
      throw new Error("No matching scenarios found for scenario group");
    }

    return this.scenarioService.runScenarioGroup(
      scenarioGroupCopy,
      scenarios,
      this.webWorkerQueue,
      callback,
      this.balanceByAccountSymbols.balances(),
      onSuperseded,
    );
  }

  /**
   * This function is used to recalculate the balances for each balance collection in
   * the calculated store.
   */
  private async updateBalanceCalculations() {
    const scenarioTransactions: TransactionView[][] = [];

    for (const scenario of this.interestScenario.collection()) {
      scenario.fullTransactionList.sort(sortTransactionViews);
      scenarioTransactions.push(scenario.fullTransactionList);
    }

    // TODO: use the granularity selection and period selection for the scenario graph when it has been built
    const parameters: ScenarioBalanceParams = {
      webWorkerQueue: this.webWorkerQueue,
      scenarioTransactions: scenarioTransactions,
      group: this.groupings.selected(),
      period: this.period.selected(),
    };

    const promises: Promise<void>[] = [];
    promises.push(this.balanceByTimeAndAccounts.updateCalculations(parameters));
    promises.push(this.balanceByTime.updateCalculations(parameters));
    promises.push(this.balanceByTransaction.updateCalculations(parameters));

    // A failure is reported only once every job has settled: the refresh then fails and the store
    // goes back to Idle, and a job still running could overwrite the next refresh's results.
    const results = await Promise.allSettled(promises);
    const [failed, ...alsoFailed] = results.filter(
      (r): r is PromiseRejectedResult => r.status === "rejected",
    );
    alsoFailed.forEach((r) => this.log.error(r.reason));
    if (failed) {
      throw failed.reason;
    }

    // This is the end
  }

  clearStore(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
    this.subscriptions = [];
    this.scenario.clear();
    this.scenarioGroup.clear();
    this.estimate.clear();
    this.estimateAction.clear();
    this.interestScenario.clear();
    this.balanceByTime.clear();
    this.balanceByTimeAndAccounts.clear();
    this.balanceByAccountSymbols.clear();
    // Read by the scenario transaction table (scenario-transaction-table.subscription.ts); left
    // populated, it shows the previous user's per-transaction balances until the next refresh.
    this.balanceByTransaction.clear();
    this.balanceAlignmentTransactions.clear();
    this.storeEpoch++;
    this.parametersPending = false;
    this._storeState$.next({ status: "Initialise" });
    this._isScenarioReady$.next(false);

    /** clear the controls **/
    this.period.clear();
    this.filters.clear();
    this.groupings.clear();

    this.log.info("ScenarioStoreService cleared");
  }

  protected getStorageCollection(modelName: StoreModelNames): StoreModelCollection {
    switch (modelName) {
      case "ScenarioGroupStoreModel":
        return this.scenarioGroup;
      case "ScenarioStoreModel":
        return this.scenario;
      case "EstimateActionStoreModel":
        return this.estimateAction;
      case "EstimateStoreModel":
        return this.estimate;
      default: {
        this.log.warning(`Need to implement ScenarioStoreService ${modelName}`);
        return this.scenario;
      }
    }
  }
}
